import { useEffect, useRef, useState } from "react";

import { IndexedDbTileInit } from "./Features/MapComponents/TileCache/indexedDB/IndexedDbTileInit";
import { AjaxTileCacheSeeder } from "./Features/MapComponents/TileCache/ajaxTileCacheSeeder";
import { BackgroundFetchTileCacheSeeder } from "./Features/MapComponents/TileCache/backgroundFetchTileCacheSeeder";
import { IndexedDbTileCache } from "./Features/MapComponents/TileCache/indexedDB/indexedDbTileCache";
import { ITileCache, ITileCacheSeeder } from "./Features/MapComponents/TileCache/types";
import { config } from "./Settings/defaults";
import { ILog } from "./Lib/Log/types";
import { IPlaceSearchService } from "./Features/Search/placeSearchTypes";
import useInstantNotifierService from "./Lib/Notifier/instantNotifierService";
import { OSPlaceSearchService } from "./Services/OSPlaceSearchService";
import useLocalStoragePlaceHistoryService from "./Services/useLocalStoragePlaceHistoryService";
import useLocalStorageSettingsService from "./Services/useLocalStorageSettingsService";
import useMapLocationTracker from "./Services/useMapLocationTracker";
import useDeviceCompass from "./Services/useDeviceCompass";
import useServiceWorkerStatus from "./Services/useServiceWorkerStatus";
import useWakeLock from "./Services/useWakeLock";
import useLocalStorageMapState from "./Services/useLocalStorageMapState";


const useApp = (log: ILog) => {

    const [isInitialising, setIsInitialising] = useState<boolean>(false);
    const [isInitialised, setIsInitialised] = useState<boolean>(false);

    const tileCache = useRef<ITileCache>();
    if (!tileCache.current)
        tileCache.current = new IndexedDbTileCache(config.tileCacheSeeder)

    const cacheSeeders = useRef<ITileCacheSeeder[]>();
    if (cacheSeeders.current == null) {
        cacheSeeders.current = [
            new AjaxTileCacheSeeder(config.tileCacheSeeder),
            new BackgroundFetchTileCacheSeeder(config.tileCacheSeeder)
        ];
    }

    const placeSearchService = new OSPlaceSearchService(config.ui.placeSearchOSApiUrl);
    const mapStateService = useLocalStorageMapState("mapState");
    const settingsService = useLocalStorageSettingsService(config, "settings", cacheSeeders.current);
    const instantNotifierService = useInstantNotifierService(config.ui.notifierMaxItems, log);

    const serviceWorkerStatus = useServiceWorkerStatus(instantNotifierService, log);

    const locationTracker = useMapLocationTracker(settingsService.settings.map, instantNotifierService, log);
    const deviceCompass = useDeviceCompass(locationTracker, log);
    const placeHistoryService = useLocalStoragePlaceHistoryService('place-history', config.ui.placeHistoryMaxItems);
    useWakeLock(locationTracker, log);

    useEffect(() => {

        if (!isInitialised && !isInitialising) {
        
            setIsInitialising(true);

            IndexedDbTileInit.run(config.tileCacheSeeder, log)
                .then(() => {
                    setIsInitialising(false);
                    setIsInitialised(true);
                    log.info('App Initialisation Done');
                })
                .catch((error) => {
                    log.error('App Initialisation Failed',error);
                    setIsInitialising(false);
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps        
    }, []);

    return {
        isInitialised,
        tileCache: tileCache.current,
        cacheSeeders: cacheSeeders.current,
        placeSearchService: placeSearchService as IPlaceSearchService,
        settingsService: settingsService,
        mapStateService: mapStateService,
        instantNotifierService: instantNotifierService,
        serviceWorkerStatus,
        locationTracker,
        deviceCompass,
        placeHistoryService
    };

};

export default useApp;